import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import { addBadges } from '../../slice/badgeSlice'

// Validation Schema
const badgeSchema = yup.object().shape({
    name: yup
        .string()
        .required('Badge name is required')
        .min(3)
        .max(100),

    description: yup
        .string()
        .required('Description is required')
        .min(10)
        .max(300),

    image: yup
        .mixed()
        .required('Badge image is required'),

    min_completed_bookings: yup
        .number()
        .required('Minimum completed bookings required')
        .min(0)
        .typeError('Must be a number'),

    min_avg_rating: yup
        .number()
        .required('Minimum rating required')
        .min(0)
        .max(5)
        .typeError('Must be a number'),

    min_hours_per_month: yup
        .number()
        .required('Minimum hours required')
        .min(0)
        .typeError('Must be a number'),
})

const AddBadge = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [preview, setPreview] = useState(null)

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset
    } = useForm({
        resolver: yupResolver(badgeSchema)
    })

    // Image preview
    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            setValue('image', file)
            setPreview(URL.createObjectURL(file))
        }
    }

    const onSubmit = async (data) => {
        setIsSubmitting(true)

        try {
            // FormData for image upload
            const formData = new FormData()
            formData.append('name', data.name)
            formData.append('description', data.description)
            formData.append('image', data.image)
            formData.append('min_completed_bookings', data.min_completed_bookings)
            formData.append('min_avg_rating', data.min_avg_rating)
            formData.append('min_hours_per_month', data.min_hours_per_month)

            const response = await dispatch(addBadges(formData))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Badge created successfully',
                    timer: 2000,
                    showConfirmButton: false
                })

                reset()
                setPreview(null)
                setTimeout(() => navigate('/badges'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to create badge'
                })
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong'
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Add Badge</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/badges')}
                >
                    ← Back to List
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-light fw-semibold">
                        Badge Information
                    </div>

                    <div className="card-body p-4">
                        <div className="row g-3">

                            {/* Name */}
                            <div className="col-md-6">
                                <label className="form-label">Badge Name *</label>
                                <input
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    {...register('name')}
                                />
                                {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                            </div>

                            {/* Image */}
                            <div className="col-md-6">
                                <label className="form-label">Badge Image *</label>
                                <input
                                    type="file"
                                    className={`form-control ${errors.image ? 'is-invalid' : ''}`}
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {errors.image && <div className="invalid-feedback">{errors.image.message}</div>}
                            </div>

                            {/* Preview */}
                            {preview && (
                                <div className="col-12">
                                    <img
                                        src={preview}
                                        alt="preview"
                                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                                    />
                                </div>
                            )}

                            {/* Description */}
                            <div className="col-12">
                                <label className="form-label">Description *</label>
                                <textarea
                                    className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                    rows="3"
                                    {...register('description')}
                                />
                                {errors.description && <div className="invalid-feedback">{errors.description.message}</div>}
                            </div>

                            {/* Conditions */}
                            <div className="col-md-4">
                                <label className="form-label">Min Completed Bookings *</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.min_completed_bookings ? 'is-invalid' : ''}`}
                                    {...register('min_completed_bookings')}
                                />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label">Min Avg Rating *</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    className={`form-control ${errors.min_avg_rating ? 'is-invalid' : ''}`}
                                    {...register('min_avg_rating')}
                                />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label">Min Hours / Month *</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.min_hours_per_month ? 'is-invalid' : ''}`}
                                    {...register('min_hours_per_month')}
                                />
                            </div>

                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/badges')}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Creating...' : 'Create Badge'}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AddBadge